import { createApp } from 'vue';
import App from './App.vue';
import router from "./router";
import AOS from 'aos';
import 'aos/dist/aos.css';
import i18n from './i18n';
import store from "./state/store";
import axiosIns from './libs/axios';
import helper from './libs/helper';
import eventBus from './libs/eventBus';

import BootstrapVueNext from 'bootstrap-vue-next';
import vClickOutside from "click-outside-vue3";
import VueApexCharts from "vue3-apexcharts";
import Maska from 'maska';

import VueFeather from 'vue-feather';

import '@/assets/scss/config/modern/app.scss';
import '@vueform/slider/themes/default.css';
import '@/assets/scss/mermaid.min.css';
import 'bootstrap/dist/js/bootstrap.bundle'

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import toastification from './libs/toastification';

import { abilitiesPlugin } from '@casl/vue';
import ability from './libs/acl/ability';

// Pusher
import Echo from 'laravel-echo';
window.Pusher = require('pusher-js');
window.Pusher.logToConsole = false;

const hasPusherConfig = Boolean(process.env.VUE_APP_PUSHER_APP_KEY);

// connect account
window.Echo = hasPusherConfig
    ? new Echo({
        broadcaster: 'pusher',
        key: process.env.VUE_APP_PUSHER_APP_KEY,
        cluster: process.env.VUE_APP_PUSHER_APP_CLUSTER,
        forceTLS: true,
    })
    : null;

// FakeBackend authentication
// import { configureFakeBackend } from './helpers/fake-backend';
// configureFakeBackend();


// Firebase authentication
// import { initFirebaseBackend } from './authUtils';

// const firebaseConfig = {
//     apiKey: process.env.VUE_APP_APIKEY,
//     authDomain: process.env.VUE_APP_AUTHDOMAIN,
//     databaseURL: process.env.VUE_APP_VUE_APP_DATABASEURL,
//     projectId: process.env.VUE_APP_PROJECTId,
//     storageBucket: process.env.VUE_APP_STORAGEBUCKET,
//     messagingSenderId: process.env.VUE_APP_MEASUREMENTID
// };

// initFirebaseBackend(firebaseConfig);

AOS.init({
    easing: 'ease-out-back',
    duration: 1000
});

const app = createApp(App);
app.use(store);
app.use(router);
app.use(VueApexCharts);
app.use(BootstrapVueNext);
app.component(VueFeather.type, VueFeather);
app.use(Maska);
app.use(i18n);
app.use(vClickOutside);
app.use(helper);
app.use(Toast, toastification)
app.use(abilitiesPlugin, ability, {
    useGlobalProperties: true
})
app.config.globalProperties.$axios = { ...axiosIns };

// Track initialization state
let notificationInitialized = false;
let currentChannel = null;
let initializationInProgress = false;

// Audio notification function
const playSound = (type = 'transaction') => {
    try {
        let audioFile;
        if (type === 'paymentAccount' || type === 'paymentLayer') {
            audioFile = require('@/assets/audio/die.mp3'); // Different sound for payment account
        } else if (type === 'systemMessage') {
            audioFile = require('@/assets/audio/msg.mp3'); // Sound for system messages
        } else {
            audioFile = require('@/assets/audio/coin.mp3'); // Default sound for deposit/withdrawal
        }

        const audio = new Audio(audioFile);
        audio.volume = 0.1; // Set volume to 10%
        audio.play().catch(error => {
            console.log('Could not play transaction notification sound:', error);
        });
    } catch (error) {
        console.log('Error loading transaction notification sound:', error);
    }
};

// Clear existing Echo listeners
const clearNotificationListeners = () => {
    if (currentChannel && window.Echo) {
        try {
            window.Echo.leaveChannel(currentChannel);
        } catch (error) {
            console.log('Error leaving channel:', error);
        }
        currentChannel = null;
    }
};

// Setup Echo listeners for deposit and withdrawal notifications
const setupNotificationListeners = () => {
    const currentTenant = store.getters['auth/currentUser']?.tenant_id || store.state.auth.currentTenant?.id;
    const currentCurrency = store.state.config.selectedCurrency?.code;

    if (currentTenant && currentCurrency && window.Echo) {
        // Clear existing listeners first
        clearNotificationListeners();

        currentChannel = `channel.${currentTenant}.${currentCurrency}`;
        // Listen for deposit notifications
        if (store.getters['auth/hasPermission']?.('pas.transaction.deposits.view')) {
            window.Echo.channel(currentChannel)
                .listen('.deposit', (data) => {

                    // Update only the deposit count
                    store.dispatch('notification/setProcessDepositCount', data.process_count || 0);

                    // Play audio notification
                    playSound();

                    // Emit event to refresh deposits table
                    eventBus.$emit('refreshDepositsTable');
                });
        }

        // Listen for withdrawal notifications
        if (store.getters['auth/hasPermission']?.('pas.transaction.withdraws.view')) {
            window.Echo.channel(currentChannel)
                .listen('.withdraw', (data) => {

                    // Update only the withdrawal count
                    store.dispatch('notification/setPendingWithdrawalCount', data.pending_count || 0);

                    // Play audio notification
                    playSound();

                    // Emit event to refresh withdrawals table
                    eventBus.$emit('refreshWithdrawalsTable');
                });

            window.Echo.channel(currentChannel)
                .listen('.withdraw_lock', () => {
                    // Emit event to refresh withdrawals table
                    eventBus.$emit('refreshWithdrawalsTable');
                });
        }

        // Listen for payment account notifications
        if (store.getters['auth/hasPermission']?.('pas.payment.accounts.view')) {
            window.Echo.channel(currentChannel)
                .listen('.paymentAccount', (data) => {

                    // Update only the withdrawal count
                    store.dispatch('notification/setCriticalAccountCount', data.critical_account_count || 0);

                    // Play audio notification
                    playSound('paymentAccount');

                    // Emit event to refresh withdrawals table
                    eventBus.$emit('refreshPaymentAccountsTable');
                });

            // Listen for individual payment account balance threshold alerts
            window.Echo.channel(currentChannel)
                .listen('.paymentAccountBalanceThreshold', (data) => {

                    // generate message
                    const message = data.is_low ? i18n.global.t('t-balance-below-threshold') : i18n.global.t('t-threshold-back-to-normal');

                    const toast = data.is_low
                        ? app.config.globalProperties.toastError
                        : app.config.globalProperties.toastSuccess

                    toast(`${data.account_name} - ${data.account_no}`, message)

                    // Play audio notification
                    playSound('paymentAccount');

                    // Refresh the notification bell's list
                    eventBus.$emit('refreshNotificationsList');
                });
        }

        // Listen for payment layer notifications
        if (store.getters['auth/hasPermission']?.('pas.payment.layers.view')) {
            window.Echo.channel(currentChannel)
                .listen('.paymentLayer', (data) => {

                    // generate message
                    const message = data.is_exceeded ? i18n.global.t('t-threshold-exceeded') : i18n.global.t('t-threshold-back-to-normal');

                    const toast = data.is_exceeded
                        ? app.config.globalProperties.toastError
                        : app.config.globalProperties.toastSuccess

                    toast(data.label, message)

                    // Play audio notification
                    playSound('paymentLayer');

                    eventBus.$emit('refreshPaymentLayerLogTableData');
                });
        }

        // Listen for system broadcast messages (no permission required)
        window.Echo.channel(currentChannel)
            .listen('.systemMessage', (data) => {
                const { title, message, message_level } = data;
                const toastMap = {
                    'success': app.config.globalProperties.toastSuccess,
                    'error': app.config.globalProperties.toastError,
                    'warning': app.config.globalProperties.toastWarning,
                    'info': app.config.globalProperties.toastInfo
                };
                const toastFunction = toastMap[message_level?.toLowerCase()] || app.config.globalProperties.toastInfo;
                toastFunction(title || 'System Message', message || '');

                // Play audio notification
                playSound('systemMessage');
            })
            .listen('.exportTask', (data) => {
                const currentUserName = store.getters['auth/currentUser']?.name;
                if (!currentUserName || data.requested_by !== currentUserName) return;

                eventBus.$emit('exportTaskUpdate', data);
                eventBus.$emit('exportTask', data);

                playSound('systemMessage');

                if (data.status === 'completed') {
                    app.config.globalProperties.toastSuccess(i18n.global.t('t-export-ready'));
                } else if (data.status === 'failed') {
                    app.config.globalProperties.toastError(i18n.global.t('t-export-failed'));
                }
            });
    }
};

// Initialize notifications only once
const initializeNotifications = async () => {
    const currentTenant = store.getters['auth/currentUser']?.tenant_id || store.state.auth.currentTenant?.id;

    if (currentTenant && !notificationInitialized && !initializationInProgress) {
        initializationInProgress = true;

        try {
            // Initialize notification counts from API
            await store.dispatch('notification/init');
            // Setup Pusher listeners
            setupNotificationListeners();
            // Mark as initialized
            notificationInitialized = true;
        } catch (error) {
            // Silent error handling
        } finally {
            initializationInProgress = false;
        }
    }
};

// Debounce timer for initialization
let initializationTimer = null;

// Setup listeners only after login
store.subscribe((mutation) => {
    // Listen for successful login
    if (mutation.type === 'auth/loginSuccess' || mutation.type === 'auth/SET_CURRENT_USER') {
        // Clear any existing timer
        if (initializationTimer) {
            clearTimeout(initializationTimer);
        }

        // Debounce initialization to prevent rapid successive calls
        initializationTimer = setTimeout(() => {
            initializeNotifications();
            initializationTimer = null;
        }, 100); // 100ms debounce
    }

    // Listen for tenant changes
    if (mutation.type === 'auth/SET_CURRENT_TENANT') {
        // Clear any existing timer
        if (initializationTimer) {
            clearTimeout(initializationTimer);
        }

        // Debounce setup to prevent rapid successive calls
        initializationTimer = setTimeout(() => {
            // Clear existing listeners and setup new ones for the new tenant
            clearNotificationListeners();
            setupNotificationListeners();
            initializationTimer = null;
        }, 100); // 100ms debounce
    }

    // Listen for currency changes
    if (mutation.type === 'config/SET_SELECTED_CURRENCY') {
        // Clear any existing timer
        if (initializationTimer) {
            clearTimeout(initializationTimer);
        }

        // Debounce setup to prevent rapid successive calls
        initializationTimer = setTimeout(() => {
            // Clear existing listeners and setup new ones for the new currency
            clearNotificationListeners();
            setupNotificationListeners();
            initializationTimer = null;
        }, 100); // 100ms debounce
    }

    // Reset initialization flag on logout
    if (mutation.type === 'auth/CLEAR_AUTH' || mutation.type === 'auth/logout') {
        // Clear any pending initialization
        if (initializationTimer) {
            clearTimeout(initializationTimer);
            initializationTimer = null;
        }

        notificationInitialized = false;
        currentChannel = null;
        initializationInProgress = false;

        // Clear existing listeners
        clearNotificationListeners();

        // Reset notification store state
        store.dispatch('notification/reset');
    }
});

// Listen for visibility changes (more reliable than focus for tab switching)
document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
        store.dispatch("notification/forceRefresh");
    }
});

router.isReady().then(async () => {
    // Fetch auth context after router is ready if user is logged in
    if (store.getters['auth/loggedIn']) {
        try {
            // Run these first
            await Promise.all([
                store.dispatch('auth/fetchContext').catch(err => console.warn('fetchContext failed', err)),
                store.dispatch('config/fetchConfig').catch(err => console.warn('fetchConfig failed', err))
            ]);

            // Only after context+config is done
            await store.dispatch('auth/fetchUserData').catch(err => console.warn('fetchUserData failed', err));
        } catch (error) {
            console.error('Bootstrap failed:', error);
        }
    }
    app.mount('#app');
});
